import {
  FormViewConfig,
  FormViewServiceProvider,
  vsProviderName as FormViewServiceProviderName,
} from "../../../records/forms/formView";
import { RecordDataSource } from "../../../records/grids";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import {
  vsServiceName as ColumnDefinitionSetServiceName,
  ColumnDefinitionSetServiceProvider,
  vsProviderName as ColumnDefinitionSetServiceProviderName,
} from "../../../records/grids/columns/columnDefinitionSetService";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../../../records/grids/settings/gridSettingsService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import {
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
} from "../../../records/views/recordGridView";
import fieldHelpers from "../../../ui-formly/fieldHelpers";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../records/forms/formly/fieldConfigurationHelpers";
import { LookupManagementService } from "../../../records/lookup/lookupManagement";
import { vsLookupManagementServiceName as MachineLookupManagementServiceName } from "../../shared/lookups";
import { vsObjectName as gameNameName } from "./lookup/gameName";
import { vsObjectName as calcTypeName } from "./lookup/calcType";
import { vsObjectName as systemTypeName } from "./lookup/systemType";
import { vsObjectName as progressiveTypeName } from "./lookup/progressiveType";
import { denominationName, themeName } from "./lookup";
import { percentageValueGetterFactory } from "../../../records/grids/cells/valueGetters";

export const vsObjectName = "machineConfigurationType";

machineConfigurationTypeConfig.$inject = [
  RecordGridViewServiceProviderName,
  ColumnDefinitionSetServiceProviderName,
  FormViewServiceProviderName,
];
export function machineConfigurationTypeConfig(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider,
  formViewServiceProvider: FormViewServiceProvider
) {
  configureGrid(
    recordGridViewServiceProvider,
    columnDefinitionSetServiceProvider
  );

  configureForm(formViewServiceProvider);
}

function configureGrid(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  columnDefinitionSetServiceProvider.addColumnDefinitionSet(vsObjectName, [
    ColumnDefinitionServiceName,
    ColumnDefinitionSetServiceName,
    (columnDefinitionService: ColumnDefinitionService) => [
      columnDefinitionService.getNumberColDef({
        field: "Id",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineConfigurationTypeName",
        header: "Configuration Type",
      }),
      columnDefinitionService.getStringColDef({
        field: "GameNameName",
        header: "Game Name",
      }),
      columnDefinitionService.getStringColDef({
        field: "GameFamily",
        header: "Game Family",
      }),
      columnDefinitionService.getStringColDef({
        field: "Denominations",
      }),
      columnDefinitionService.getStringColDef({
        field: "Description",
      }),
      columnDefinitionService.getBooleanColDef({
        field: "Coinless",
      }),
      columnDefinitionService.getBooleanColDef({
        field: "MultiGame",
        header: "Multi Game",
      }),
      columnDefinitionService.getBooleanColDef({
        field: "MultiDenom",
        header: "Multi Denom",
      }),
      columnDefinitionService.getBooleanColDef({
        field: "Poker",
      }),
      columnDefinitionService.getStringColDef({
        field: "ETG",
      }),
      columnDefinitionService.getNumberColDef({
        field: "MachineDenominationName",
        header: "Accounting Denomination",
      }),
      columnDefinitionService.getStringColDef({
        field: "CalcTypeName",
        header: "Calc Type",
      }),
      columnDefinitionService.getStringColDef({
        field: "SystemTypeName",
        header: "System Type",
      }),
      columnDefinitionService.getNumberColDef({
        field: "MachineConfigurationTypePar",
        header: "Par",
        valueGetter: percentageValueGetterFactory(),
      }),
      columnDefinitionService.getNumberColDef({
        field: "FirstCoinPar",
        header: "First Coin Par",
        valueGetter: percentageValueGetterFactory(),
      }),
      columnDefinitionService.getNumberColDef({
        field: "MaxCoinPar",
        header: "Max Coin Par",
        valueGetter: percentageValueGetterFactory(),
      }),
      columnDefinitionService.getFileColDef({
        field: "ParSheet",
        header: "Par Sheet",
        hide: true,
      }),
      columnDefinitionService.getFileColDef({
        field: "MDMG",
        hide: true,
      }),
    ],
  ]);

  recordGridViewServiceProvider.addGridConfiguration(vsObjectName, [
    ServerUtilityName,
    GridSettingsServiceName,
    (
      serverUtility: ServerUtility,
      gridSettingsService: GridSettingsService
    ) => {
      return {
        columnDefinitionSet: vsObjectName,
        defaultSort: "MachineConfigurationTypeName",
        gridDataSource: new RecordDataSource(
          serverUtility,
          "/api/machines/MachineConfigurationType"
        ),
        nameSingular: "Configuration Type",
        gridSettings: gridSettingsService.getGridSettings(Symbol(vsObjectName)),
        showAddButton: true,
        showAddButtonPermission: "machineEdit",
      };
    },
  ]);
}
function configureForm(formViewServiceProvider: FormViewServiceProvider) {
  formViewServiceProvider.addFormViewConfig(vsObjectName, [
    FieldConfigurationHelperServiceName,
    MachineLookupManagementServiceName,
    (
      fieldConfigurationHelperService: FieldConfigurationHelperService,
      machineLookupManagementService: LookupManagementService
    ): FormViewConfig => {
      return {
        fields: [
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleStringField({
                key: "Name",
                label: "Name",
                required: true,
              }),
            ],
            4
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: "GameNameId",
                  label: "Game Name",
                  objectName: gameNameName,
                }
              ),
              fieldHelpers.simpleStringField({
                key: "GameFamily",
                label: "Game Family",
              }),
            ],
            4
          ),
          {
            className: "row",
            fieldGroup: [
              fieldHelpers.simpleStringField({
                key: "Description",
                label: "Description",
                required: true,
                className: "col-xs-6",
              }),
              fieldHelpers.simpleBoolField({
                key: "Coinless",
                label: "Coinless",
                defaultValue: false,
                className: "col-xs-3",
              }),
            ],
          },
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleBoolField({
                key: "MultiGame",
                label: "Multi Game",
                defaultValue: false,
              }),
              fieldHelpers.simpleBoolField({
                key: "MultiDenom",
                label: "Multi Denom",
                defaultValue: false,
              }),
              fieldHelpers.simpleBoolField({
                key: "Poker",
                label: "Poker",
                defaultValue: false,
              }),
              fieldHelpers.simpleBoolField({
                key: "ETG",
                label: "ETG",
                defaultValue: false,
              }),
            ],
            4
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: "MachineDenominationId",
                  label: "Accounting Denomination",
                  objectName: denominationName,
                }
              ),
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: "CalcTypeId",
                  label: "Calc Type",
                  objectName: calcTypeName,
                }
              ),
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: "SystemTypeId",
                  label: "System Type",
                  objectName: systemTypeName,
                }
              ),
            ],
            4
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simplePercentageField({
                key: "Par",
                label: "Par",
              }),
              fieldHelpers.simplePercentageField({
                key: "FirstCoinPar",
                label: "First Coin Par",
              }),
              fieldHelpers.simplePercentageField({
                key: "MaxCoinPar",
                label: "Max Coin Par",
              }),
            ],
            4
          ),

          fieldConfigurationHelperService.repeaterField({
            key: "GameConfigs",
            allowAdd: true,
            allowAddBottom: true,
            displayNameSingular: "Game Math",
            displayNamePlural: "Games Math",
            repeaterFields: fieldHelpers.columnLayout(
              [
                fieldConfigurationHelperService.lookupField(
                  machineLookupManagementService,
                  {
                    key: "MachineThemeId",
                    label: "Theme",
                    objectName: themeName,
                  }
                ),
                fieldConfigurationHelperService.lookupField(
                  machineLookupManagementService,
                  {
                    key: "MachineDenominationId",
                    label: "Denomination",
                    objectName: denominationName,
                  }
                ),
                fieldHelpers.simpleNumberField({
                  key: "Par",
                  label: "Par",
                }),
                fieldHelpers.simplePercentageField({
                  key: "MaxCoin",
                  label: "Max Coin",
                }),
                fieldHelpers.simplePercentageField({
                  key: "FirstCoin",
                  label: "First Coin",
                }),

                fieldHelpers.simpleNumberField({
                  key: "MaxMachPay",
                  label: "Max Mach Pay",
                }),
                fieldHelpers.simpleNumberField({
                  key: "MaxCoins",
                  label: "Max Coins",
                }),
                fieldHelpers.simpleNumberField({
                  key: "MinBet",
                  label: "Min Bet",
                }),
                fieldHelpers.simpleNumberField({
                  key: "MaxBet",
                  label: "Max Bet",
                }),
                fieldHelpers.simpleNumberField({
                  key: "PayLine",
                  label: "Payline",
                }),
                fieldHelpers.simplePercentageField({
                  key: "NetWin",
                  label: "Net Win",
                }),
                fieldHelpers.simplePercentageField({
                  key: "CoinIn",
                  label: "Coin In",
                }),
                fieldHelpers.simpleNumberField({
                  key: "DailyFee",
                  label: "Daily Fee",
                }),
                fieldHelpers.simpleBoolField({
                  key: "Wap",
                  label: "WAP",
                  defaultValue: false,
                }),
                fieldHelpers.simpleStringField({
                  key: "PaytableId",
                  label: "PayTable ID",
                }),
                {
                  key: "copyDelete",
                  type: "recordRepeaterCopyDelete",
                  className: "col-xs-6",
                  templateOptions: {
                    label: " ",
                    allowMove: true,
                  },
                },
              ],
              4
            ),
            showHeader: true,
          }),

          fieldConfigurationHelperService.repeaterField({
            key: "ProgressiveGame",
            allowAdd: true,
            allowAddBottom: false,
            displayNameSingular: "Progressive Game",
            displayNamePlural: "Progressive Game",
            repeaterFields: fieldHelpers.columnLayout(
              [
                fieldHelpers.simpleStringField({
                  key: "Name",
                  label: "Name",
                }),

                fieldConfigurationHelperService.repeaterField({
                  key: "ProgressiveLevels",
                  allowAdd: true,
                  allowAddBottom: false,
                  displayNameSingular: "Progressive Levels",
                  displayNamePlural: "Progressive Levels",
                  repeaterFields: [
                    ...fieldHelpers.columnLayout(
                      [
                        fieldHelpers.simpleStringField({
                          key: "Name",
                          label: "Name",
                        }),
                        fieldHelpers.simplePercentageField({
                          key: "Percentage",
                          label: "Percentage",
                        }),
                        fieldHelpers.simpleCurrencyField({
                          key: "ResetBase",
                          label: "Reset Base",
                        }),
                        fieldHelpers.simpleCurrencyField({
                          key: "Ceiling",
                          label: "Ceiling",
                        }),
                      ],
                      4
                    ),
                    {
                      fieldGroup: [
                        fieldHelpers.simpleBoolField({
                          key: "MustHitBy",
                          label: "Must Hit By",
                          defaultValue: false,
                          className: "col-xs-3",
                        }),
                        fieldConfigurationHelperService.lookupField(
                          machineLookupManagementService,
                          {
                            key: "ProgressiveTypeId",
                            label: "Progressive Type",
                            objectName: progressiveTypeName,
                            className: "col-xs-3",
                          }
                        ),
                        {
                          key: "copyDelete",
                          type: "recordRepeaterCopyDelete",
                          className: "col-xs-6",
                          templateOptions: {
                            label: " ",
                            allowMove: true,
                          },
                        },
                      ],
                      className: "row",
                    },
                  ],
                  showHeader: true,
                }),
                {
                  key: "copyDelete",
                  type: "recordRepeaterCopyDelete",
                  className: "col-xs-12",
                  templateOptions: {
                    label: " ",
                    allowMove: true,
                  },
                },
              ],
              1
            ),
            showHeader: true,
          }),

          fieldHelpers.headerField({
            headerText: "Files",
            key: "FileHeader",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleSingleFileUpload({
                key: "ParSheet",
                label: "Par Sheet",
              }),
              fieldHelpers.simpleSingleFileUpload({
                key: "MDMG",
                label: "MDMG",
              }),
            ],
            4
          ),
          {
            key: "Files",
            type: "recordFileUpload",
            templateOptions: {
              label: "Files",
              allowMultiple: true,
              hideLabel: true,
            },
          },
          fieldHelpers.headerField({
            headerText: "Hyperlinks",
            key: "HyperlinksHeader",
          }),
          fieldConfigurationHelperService.repeaterField({
            allowAdd: true,
            allowAddBottom: false,
            displayNameSingular: "Hyperlink",
            displayNamePlural: "Hyperlinks",
            label: "Hyperlinks",
            key: "Hyperlinks",
            name: "Hyperlinks",
            repeaterFields: [
              ...fieldHelpers.columnLayout(
                [
                  fieldHelpers.simpleStringField({
                    key: "DisplayText",
                    name: "DisplayText",
                    label: "Display Text",
                    required: true,
                  }),
                  fieldHelpers.simpleStringField({
                    key: "Url",
                    name: "Url",
                    label: "URL",
                    required: true,
                  }),
                  {
                    key: "copyDelete",
                    type: "recordRepeaterCopyDelete",
                    templateOptions: {
                      label: " ",
                      allowMove: false,
                    },
                  },
                ],
                4
              ),
            ],
            showHeader: false,
          }),
        ],
        name: vsObjectName,
        title: () => "Configuration Type",
        showButtonsAtTop: true,
      };
    },
  ]);
}
